import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { UserPlus, Loader2, Check, Clock, Mail, Heart } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function InviteConnections() {
  const { user } = useApp();
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/access/my-invites");
      setInvites(data.invites || []);
      setStats({ total: data.total || 0, approved: data.approved || 0, pending: data.pending || 0 });
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const send = async () => {
    if (!emails.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/access/invite", { emails });
      toast.success(`${data.added} invited to the waitlist${data.skipped ? ` · ${data.skipped} already known` : ""}${data.invalid ? ` · ${data.invalid} skipped` : ""}`);
      setEmails(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (user === undefined) return <div className="min-h-screen grid place-items-center text-sec"><HeartLoader /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-16" data-testid="invite-connections-page">
      <div className="flex items-center gap-2 text-brand mb-1"><Heart size={18} /> <span className="text-xs uppercase tracking-widest">Grow the family</span></div>
      <h1 className="font-serif text-3xl tracking-tight">Invite your connections</h1>
      <p className="text-sec text-sm mt-1">Add the emails of people you'd love to bring in. They'll join the <b className="text-[var(--rud-text)]">waitlist</b> — a founder approves each one before they can sign in. <span className="text-brand">Vasudhaiva Kutumbakam</span> — the world is one family. 🌍</p>

      <div className="mt-6 rounded-2xl border border-brand/25 bg-brand/5 p-4">
        <div className="text-sm font-medium flex items-center gap-2 mb-2"><UserPlus size={16} className="text-brand" /> Invite by email</div>
        <textarea data-testid="invite-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)} rows={3}
          placeholder="friend@example.com, family@example.com&#10;colleague@example.com" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-y" />
        <button data-testid="invite-send-btn" onClick={send} disabled={busy || !emails.trim()} className="btn-primary rounded-lg px-5 py-2.5 text-sm mt-2 flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Add to waitlist
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <div className="rounded-xl border border-rud bg-white/5 p-3 text-center"><div className="text-xl font-serif" data-testid="my-invite-total">{stats.total}</div><div className="text-[11px] text-sec">Invited</div></div>
        <div className="rounded-xl border border-rud bg-white/5 p-3 text-center"><div className="text-xl font-serif text-brand" data-testid="my-invite-approved">{stats.approved}</div><div className="text-[11px] text-sec">Approved</div></div>
        <div className="rounded-xl border border-rud bg-white/5 p-3 text-center"><div className="text-xl font-serif" data-testid="my-invite-pending">{stats.pending}</div><div className="text-[11px] text-sec">Waiting</div></div>
      </div>

      <div className="mt-6">
        <div className="text-sm font-medium mb-2">People you invited</div>
        {loading ? (
          <div className="py-8 text-center text-sec"><HeartLoader /></div>
        ) : invites.length === 0 ? (
          <p className="py-8 text-center text-sec text-sm">No invites yet — add a few emails above to grow the family. 💛</p>
        ) : (
          <div className="space-y-2" data-testid="my-invites-list">
            {invites.map((it) => (
              <div key={it.email} className="flex items-center gap-3 rounded-lg border border-rud bg-white/5 px-4 py-2.5">
                <div className="flex-1 min-w-0 text-sm truncate">{it.name ? <><b className="text-[var(--rud-text)]">{it.name}</b> · {it.email}</> : it.email}</div>
                {it.status === "approved"
                  ? <span className="text-[11px] text-brand flex items-center gap-1"><Check size={13} /> Joined</span>
                  : <span className="text-[11px] text-sec flex items-center gap-1"><Clock size={13} className="text-amber-400" /> Waiting</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
